import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { makeStyles, Text, Button, Spinner, Field } from '@fluentui/react-components';
import { ArrowLeftRegular, ArrowSyncRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, SearchableDropdown } from '../../../components/common';
import { ApiClient, getErrorMessage, newToast } from '../../../configurations/index.config';
import type { QRAsset, Site, Checkpoint } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  currentAssignment: {
    padding: '16px',
    backgroundColor: '#fef6e6',
    border: '1px solid #fce100',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '600px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '24px',
    borderTop: '1px solid #edebe9',
  },
});

const ReassignQRFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [reassigning, setReassigning] = useState(false);
  const [qrAsset, setQrAsset] = useState<QRAsset | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);

  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<string>('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchCheckpoints(Number(selectedSiteId));
    } else {
      setCheckpoints([]);
      setSelectedCheckpointId('');
    }
  }, [selectedSiteId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [qrRes, sitesRes] = await Promise.all([
        ApiClient.get(`/get-qr-asset/${id}`),
        ApiClient.get('/get-sites'),
      ]);

      if (qrRes.data.status === 'success') {
        const asset = qrRes.data.data;
        setQrAsset(asset);

        if (asset.assigned_site_id) {
          setSelectedSiteId(asset.assigned_site_id.toString());
        }
      }

      if (sitesRes.data.status === 'success') {
        setSites(sitesRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
      navigate('/account/qr-assets');
    }
    setLoading(false);
  };

  const fetchCheckpoints = async (siteId: number) => {
    try {
      const response = await ApiClient.get('/get-checkpoints', {
        params: { site_id: siteId, status: 'active' },
      });

      if (response.data.status === 'success') {
        setCheckpoints(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleReassign = async () => {
    if (!selectedSiteId || !selectedCheckpointId) {
      newToast('error', 'Please select both site and checkpoint');
      return;
    }

    if (qrAsset?.assigned_checkpoint_id === Number(selectedCheckpointId)) {
      newToast('error', 'QR is already assigned to this checkpoint');
      return;
    }

    setReassigning(true);
    try {
      const response = await ApiClient.post(`/reassign-qr/${id}`, {
        new_checkpoint_id: Number(selectedCheckpointId),
        reason: reason || undefined,
      });

      if (response.data.status === 'success') {
        newToast('success', 'QR code reassigned successfully');
        navigate('/account/qr-assets');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setReassigning(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading QR asset..." />
      </div>
    );
  }

  if (!qrAsset) {
    return null;
  }

  return (
    <PermissionGate privilege="qr-assets.assign" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate(`/account/qr-asset-details/${id}`)}
          />
          <Text className={styles.pageTitle}>Reassign QR Code</Text>
        </div>

        <ContentCard>
          <div className={styles.currentAssignment}>
            <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
              Current Assignment
            </Text>
            <Text style={{ display: 'block' }}>{qrAsset.qr_name} is currently assigned to:</Text>
            <Text weight="semibold" style={{ display: 'block', marginTop: '4px' }}>
              {qrAsset.assigned_checkpoint_name} - {qrAsset.assigned_site_name}
            </Text>
          </div>

          <div className={styles.form}>
            <Field label="Select New Site" required>
              <SearchableDropdown
                placeholder="Choose a site"
                selectedValue={selectedSiteId}
                onSelect={setSelectedSiteId}
                options={sites.map(site => ({
                  id: site.id.toString(),
                  label: site.name,
                }))}
                required
              />
            </Field>

            <Field label="Select New Checkpoint" required>
              <SearchableDropdown
                placeholder={selectedSiteId ? 'Choose a checkpoint' : 'Select a site first'}
                selectedValue={selectedCheckpointId}
                onSelect={setSelectedCheckpointId}
                options={checkpoints
                  .filter(cp => cp.id !== qrAsset.assigned_checkpoint_id)
                  .map(checkpoint => ({
                    id: checkpoint.id.toString(),
                    label: checkpoint.checkpoint_name,
                  }))}
                disabled={!selectedSiteId || checkpoints.length === 0}
                required
              />
            </Field>

            {selectedSiteId && checkpoints.length === 0 && (
              <Text size={200} style={{ color: '#a4262c' }}>
                No active checkpoints found for this site
              </Text>
            )}

            <Field
              label="Reason for Reassignment"
              required
              hint="Explain why this QR is being moved"
            >
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  border: '1px solid #d1d1d1',
                  borderRadius: '4px',
                }}
              />
            </Field>
          </div>

          <div className={styles.actions}>
            <Button
              appearance="secondary"
              onClick={() => navigate(`/account/qr-asset-details/${id}`)}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<ArrowSyncRegular />}
              onClick={handleReassign}
              disabled={reassigning || !selectedSiteId || !selectedCheckpointId || !reason.trim()}
            >
              {reassigning ? 'Reassigning...' : 'Reassign QR Code'}
            </Button>
          </div>
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default ReassignQRFluentUI;
